'use client';

/**
 * Capacitor Native Bridge
 *
 * Central init for all native Capacitor plugins.
 * Hides the splash screen once the web app is ready,
 * configures the status bar, and starts push notifications.
 *
 * Import and call `initCapacitor()` from the mobile layout or
 * top-level mobile component after initial render.
 */

import { Capacitor } from '@capacitor/core';
// Static imports, deliberately NOT runtime import(): async chunk loads hang
// inside the WKWebView shell (field-confirmed on iOS, 2026-07-03). These
// modules are tiny bridge proxies, safe on web/SSR — nothing native executes
// until a method call, and initCapacitor() guards on isNativePlatform().
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { initPushNotifications } from './native-push';

/**
 * The login path to redirect to for the current platform. On the native app the
 * Capacitor WebView is scoped to `/mobile` (next.config server.url), so a
 * full-page nav to the out-of-scope `/login` gets shunted to Safari and bounces
 * the user out of the app; `/mobile/login` stays in-app. On web it's the normal
 * `/login`. Mirrors the provider app's MobileAuthContext sign-out redirect.
 */
export function loginRedirectPath(): string {
  return Capacitor.isNativePlatform() ? '/mobile/login' : '/login';
}

let _initialized = false;

/**
 * Initialize all Capacitor native plugins.
 * Safe to call on web — gracefully no-ops when not native.
 * Returns the push-init outcome so the diagnostic beacon can report it.
 */
export async function initCapacitor(): Promise<{ push: { success: boolean; error?: string } }> {
  if (_initialized) return { push: { success: false, error: 'already-initialized' } };
  if (!Capacitor.isNativePlatform()) return { push: { success: false, error: 'not-native' } };

  _initialized = true;

  // ── Status Bar ──────────────────────────────────────────────────────────
  // Fire-and-forget: cosmetic, and a hung native call here must never block
  // push init below (field evidence 2026-07-03: shell plugin calls can hang).
  StatusBar.setStyle({ style: Style.Dark }) // light text on dark bg
    .then(() => StatusBar.setBackgroundColor({ color: '#000000' }))
    .catch(() => console.warn('[Capacitor] StatusBar plugin not available'));

  // ── Splash Screen ──────────────────────────────────────────────────────
  try {
    // Give the web app a moment to render, then hide splash
    setTimeout(async () => {
      try {
        await SplashScreen.hide({ fadeOutDuration: 300 });
      } catch {
        // Ignore
      }
    }, 1500);
  } catch {
    console.warn('[Capacitor] SplashScreen plugin not available');
  }

  // ── Push Notifications ─────────────────────────────────────────────────
  try {
    const result = await initPushNotifications();
    return { push: { success: result.success, error: result.error } };
  } catch (err) {
    console.warn('[Capacitor] Push notifications init failed');
    return { push: { success: false, error: err instanceof Error ? err.message : 'init-threw' } };
  }
}
